/**
 * 個人レポート v2 の算出ロジック。
 *
 * 正典: apps/web/docs/新方針20260821/仕様_GB人財レポートの生成ロジック.html
 * 文言: apps/web/docs/新方針20260821/GB人財_個人レポート文言DB_v4_3.json（PA01〜PA16）
 *
 * 【現行(gb74Report.ts)との関係】
 *  現行は温存し、こちらを新設している。設問データ（ハーマン24問・Big5 28問）は
 *  現行とまったく同じものを使う。変わるのは「回答から何を算出するか」だけ。
 *  Big5 は現行10ファセットのうち情緒安定性を除いた9ファセット（28問）が仕様と一致する。
 *
 * 【暫定パラメータ】
 *  SD_TYP / CURIOSITY_HIGH は実測待ちの暫定値（仕様書08節）。
 *  実データが貯まったらこのファイルの定数だけを直す。
 */
import {
  BIG5_FACTORS,
  getFactorScore,
  getAxisScores,
  type AxisKey,
  type Gb74Scores,
} from './gb74Report';

// ===== 暫定パラメータ（仕様書08節・実測待ち）=====

/** 役割適性を個人内で相対化するときの想定SD。仕様書の暫定値。 */
export const SD_TYP = 0.4;
/**
 * 「力が出る役割」として出す件数。上位から固定でこの件数。
 *
 * 文言DB v4.3 の 05-RULE-上位2（2026-08-20改訂）で、旧ルール（隣接差12点で切る）は
 * 廃止された。理由は同エントリのとおり:
 *  ① 6,000人シミュレーションで隣接差の測定誤差が12.0点あり、12点で切ることは
 *    誤差と同じ幅で切ることになる（半分は偶然の差）。
 *  ② 12点で切ると18%の人が1群にまとまり「絞り込めません」となる。
 * 仕様書HTML側は改訂前の記述なので、こちらのDBルールを正とする。
 */
export const TOP_ROLE_COUNT = 2;
/** 知的好奇心を「高い」とみなす閾値。 */
export const CURIOSITY_HIGH = 3.5;
/** 4軸で「優勢」とみなす点数。 */
export const AXIS_DOMINANT = 7;
/** 4軸で S バッジ（目盛りを超える）を付ける点数。 */
export const AXIS_STRONG = 11;

// ===== 型 =====

export type FacetKey =
  | 'initiative'
  | 'execution'
  | 'consideration'
  | 'challenge'
  | 'order'
  | 'emotionControl'
  | 'integrity'
  | 'sociability'
  | 'curiosity';

/** セクション02「持ち味の出方」の4まとまり。 */
export type TraitKey = '前に出る力' | 'やり切る力' | '人を思う力' | '新しさへの構え';

export type RoleName = '管理統率' | '変革推進' | '安定運用' | '協働調整' | '橋渡し' | '承継適性';

export interface TraitScore {
  key: TraitKey;
  /** 1.00〜5.00 */
  value: number;
}

export interface RoleScoreV2 {
  name: RoleName;
  /** 合成した生スコア（1〜5相当） */
  raw: number;
  /** 個人内で相対化した表示スコア 0〜100 */
  score: number;
  /** 上位グループ（力が出る役割）か */
  top: boolean;
}

export interface Gb74ReportV2 {
  axis: Record<AxisKey, number>;
  /** 7点以上の軸を点数の高い順に並べたもの（例: ['D','C']） */
  dominantAxes: AxisKey[];
  /** タイプコード（例: 'DC'）。優勢軸が無ければ '全方型' */
  typeCode: string;
  /** 11点以上の軸（Sバッジ対象） */
  strongAxes: AxisKey[];
  /** 最低軸 */
  lowestAxis: AxisKey;
  /** Big5 9ファセット（1.00〜5.00） */
  facets: Record<FacetKey, number>;
  /** 4まとまり（表示順） */
  traits: TraitScore[];
  /** 最も高いまとまり / 最も低いまとまり */
  traitTop: TraitKey;
  traitLow: TraitKey;
  /** 均衡度（橋渡しにのみ使う） */
  balance: number;
  /** 役割適性（表示スコアの降順） */
  roles: RoleScoreV2[];
  /** 上位グループの役割名 */
  topRoles: RoleName[];
  /** 根拠を添える対象（上位1つ・最下位1つ） */
  roleReasonTop: RoleName;
  roleReasonLow: RoleName;
  /** セクション03: 考え方と行動の順位差（最大 |d|） */
  faceMaxDiff: number;
  /** |d| <= 1 が4組なら揃っている */
  faceAligned: boolean;
  /** 伸びしろ判定に使う 知的好奇心 × D創造 の2×2 */
  growth: { curiosityHigh: boolean; creativeHigh: boolean };
}

// ===== 算出 =====

const FACET_KEYS: FacetKey[] = [
  'initiative',
  'execution',
  'consideration',
  'challenge',
  'order',
  'emotionControl',
  'integrity',
  'sociability',
  'curiosity',
];

/** Big5 9ファセットを算出（現行の設問定義をそのまま使う）。 */
export function getFacets(scores: Gb74Scores): Record<FacetKey, number> {
  const out = {} as Record<FacetKey, number>;
  FACET_KEYS.forEach((k) => {
    const f = BIG5_FACTORS.find((x) => x.key === k);
    out[k] = f ? getFactorScore(scores, f) : 0;
  });
  return out;
}

/** 4まとまり＝2ファセットの平均（仕様書03節）。表示順は固定。 */
export function getTraits(f: Record<FacetKey, number>): TraitScore[] {
  return [
    { key: '前に出る力', value: (f.initiative + f.sociability) / 2 },
    { key: 'やり切る力', value: (f.execution + f.order) / 2 },
    { key: '人を思う力', value: (f.consideration + f.integrity) / 2 },
    { key: '新しさへの構え', value: (f.challenge + f.curiosity) / 2 },
  ];
}

/** 均衡度 = 5 −（4軸のレンジ ÷ 12 × 4）。橋渡しにのみ使う。 */
export function getBalance(axis: Record<AxisKey, number>): number {
  const vals = [axis.A, axis.B, axis.C, axis.D];
  const range = Math.max(...vals) - Math.min(...vals);
  return 5 - (range / 12) * 4;
}

/**
 * 役割適性の合成（仕様書04節・手順2）。
 * 係数は仕様書のとおり。変更するときは仕様書と併せて直す。
 */
export function getRoleRaw(f: Record<FacetKey, number>, balance: number): Record<RoleName, number> {
  return {
    管理統率: f.initiative * 0.4 + f.execution * 0.35 + f.emotionControl * 0.25,
    変革推進: f.challenge * 0.55 + f.initiative * 0.45,
    安定運用: f.order * 0.5 + f.execution * 0.35 + f.emotionControl * 0.15,
    協働調整: f.consideration * 0.45 + f.emotionControl * 0.3 + f.integrity * 0.25,
    橋渡し: f.sociability * 0.5 + balance * 0.5,
    承継適性:
      Math.min(f.initiative, f.execution, f.emotionControl, f.integrity) * 0.55 +
      f.integrity * 0.25 +
      f.emotionControl * 0.2,
  };
}

/**
 * 各役割にしか使われていない「固有ファセット」。根拠文(PA10)はこれだけを使う。
 * 共有ファセットを根拠にすると「主導性が強いのに管理統率は低い」のような
 * 矛盾した説明になるため（仕様書04節・手順5）。
 */
export const ROLE_UNIQUE_FACET: Record<RoleName, FacetKey> = {
  管理統率: 'initiative',
  変革推進: 'challenge',
  安定運用: 'order',
  協働調整: 'consideration',
  橋渡し: 'sociability',
  承継適性: 'integrity',
};

/** 4軸と役割の対応（セクション03の突き合わせ用）。 */
const AXIS_ROLE_PAIR: Array<{ axis: AxisKey; role: RoleName }> = [
  { axis: 'D', role: '変革推進' },
  { axis: 'C', role: '協働調整' },
  { axis: 'A', role: '管理統率' },
  { axis: 'B', role: '安定運用' },
];

/** gb74 scores から v2 レポートデータ一式を算出する。 */
export function buildGb74ReportV2(scores: Gb74Scores): Gb74ReportV2 {
  const axis = getAxisScores(scores);
  const facets = getFacets(scores);
  const traits = getTraits(facets);
  const balance = getBalance(axis);

  // --- タイプコード: 7点以上を点数の高い順（同点はA→B→C→Dで安定させる） ---
  const order: AxisKey[] = ['A', 'B', 'C', 'D'];
  const dominantAxes = order
    .filter((k) => axis[k] >= AXIS_DOMINANT)
    .sort((a, b) => axis[b] - axis[a] || order.indexOf(a) - order.indexOf(b));
  const typeCode = dominantAxes.length > 0 ? dominantAxes.join('') : '全方型';
  const strongAxes = order.filter((k) => axis[k] >= AXIS_STRONG);
  const lowestAxis = order.reduce((lo, k) => (axis[k] < axis[lo] ? k : lo), 'A' as AxisKey);

  // --- 持ち味の出方: 最上位/最下位（同点は表示順で決める） ---
  const traitSorted = [...traits];
  const traitTop = traitSorted.reduce((a, b) => (b.value > a.value ? b : a)).key;
  const traitLow = traitSorted.reduce((a, b) => (b.value < a.value ? b : a)).key;

  // --- 役割適性: 合成 → 個人内で相対化 → 降順 ---
  const raw = getRoleRaw(facets, balance);
  const names = Object.keys(raw) as RoleName[];
  const mean = names.reduce((s, n) => s + raw[n], 0) / names.length;
  const scored: RoleScoreV2[] = names
    .map((n) => ({
      name: n,
      raw: raw[n],
      score: Math.max(0, Math.min(100, Math.round(50 + ((raw[n] - mean) / SD_TYP) * 15))),
      top: false,
    }))
    .sort((a, b) => b.score - a.score);

  // 上位2つを固定で「力が出る役割」とする（文言DB 05-RULE-上位2）。
  scored.forEach((r, i) => (r.top = i < TOP_ROLE_COUNT));
  const topRoles = scored.filter((r) => r.top).map((r) => r.name);

  // --- セクション03: 4軸の順位と役割の順位を突き合わせる ---
  const axisRank = [...AXIS_ROLE_PAIR]
    .sort((a, b) => axis[b.axis] - axis[a.axis])
    .map((p) => p.axis);
  const roleRank = scored.map((r) => r.name);
  let faceMaxDiff = 0;
  AXIS_ROLE_PAIR.forEach((p) => {
    const ra = axisRank.indexOf(p.axis);
    const rr = roleRank.indexOf(p.role);
    if (ra >= 0 && rr >= 0) faceMaxDiff = Math.max(faceMaxDiff, Math.abs(rr - ra));
  });

  return {
    axis,
    dominantAxes,
    typeCode,
    strongAxes,
    lowestAxis,
    facets,
    traits,
    traitTop,
    traitLow,
    balance,
    roles: scored,
    topRoles,
    roleReasonTop: scored[0].name,
    roleReasonLow: scored[scored.length - 1].name,
    faceMaxDiff,
    faceAligned: faceMaxDiff <= 1,
    growth: {
      curiosityHigh: facets.curiosity >= CURIOSITY_HIGH,
      creativeHigh: axis.D >= AXIS_DOMINANT,
    },
  };
}
